import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { exportComplianceReport } from "@/lib/compliance-reports.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/compliance-reports")({
  head: () => ({ meta: [{ title: "Compliance reports — Admin" }] }),
  component: ComplianceReportsPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const REPORTS = [
  { key: "suspensions", label: "Suspensions audit", dateRange: true },
  { key: "incidents", label: "Incident reports", dateRange: true },
  { key: "tip_allocations", label: "Tip allocations", dateRange: true },
  { key: "rtw_snapshot", label: "RTW snapshot (current)", dateRange: false },
  { key: "insurance_snapshot", label: "Insurance snapshot (current)", dateRange: false },
] as const;

function ComplianceReportsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = today.slice(0, 4) + "-01-01";
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);

  const mut = useMutation({
    mutationFn: exportComplianceReport,
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin · Compliance</p>
        <h1 className="text-3xl font-light">Compliance reports</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          One-click CSV exports for suspensions, upheld incidents, tip allocations, plus
          point-in-time snapshots of RTW and insurance status across all active bartenders.
        </p>

        <div className="mt-6 grid gap-3 border border-border p-6 sm:grid-cols-2">
          <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <p className="sm:col-span-2 text-xs text-muted-foreground">Date range applies only to time-series reports; snapshots reflect the current state.</p>
        </div>

        <ul className="mt-6 divide-y divide-border border border-border">
          {REPORTS.map((r) => (
            <li key={r.key} className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="text-sm">{r.label}</p>
                <p className="text-xs text-muted-foreground">{r.dateRange ? "Time-windowed" : "Point-in-time snapshot"}</p>
              </div>
              <Button variant="outline" disabled={mut.isPending} onClick={() => mut.mutate({ data: { report: r.key, ...(r.dateRange ? { from, to } : {}) } })}>
                Download CSV
              </Button>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
